import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../Components/Sidebar.tsx';

// --- Styles ---
const layoutStyle = {
  display: 'flex',
  height: '100vh',
  width: '100vw',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)',
};

const mainContainerStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column' as const,
  padding: '20px',
  overflowY: 'auto',
  marginLeft: '280px',
  background: 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)',
};

const cardStyle = {
  background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.8)',
  padding: '24px',
  marginBottom: '20px',
  display: 'flex',
  flexDirection: 'column' as const,
  border: '1px solid #e2e8f0',
  transition: 'transform 0.2s, box-shadow 0.2s',
};

const sectionTitleStyle = {
  fontSize: '18px',
  fontWeight: 600,
  color: '#1976d2',
  marginBottom: '16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: '2px solid #e3f2fd',
  paddingBottom: '8px',
};

const fieldRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '10px 0',
  borderBottom: '1px solid #f1f5f9',
  transition: 'background-color 0.2s',
};

const labelStyle = {
  fontSize: '14px',
  color: '#555',
  fontWeight: 500,
  minWidth: '120px',
  flexShrink: 0,
};

const valueStyle = {
  fontSize: '14px',
  color: '#333',
  wordBreak: 'break-word',
  textAlign: 'right' as const,
  flex: 1,
  marginLeft: '12px',
};

const imageContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  marginBottom: '18px',
  padding: '12px',
  background: 'rgba(241, 245, 249, 0.5)',
  borderRadius: '12px',
};

const profileImageStyle = {
  width: 110,
  height: 110,
  borderRadius: '50%',
  objectFit: 'cover' as const,
  border: '3px solid #e3f2fd',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  transition: 'transform 0.3s',
};

const documentImageStyle = {
  width: 'auto',
  maxHeight: 220,
  borderRadius: '10px',
  border: '2px solid #e2e8f0',
  cursor: 'pointer',
  transition: 'transform 0.3s, box-shadow 0.3s',
  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
};

const actionContainerStyle = {
  display: 'flex',
  gap: '14px',
  alignItems: 'center',
  marginTop: '14px',
  padding: '8px',
  background: 'rgba(227, 242, 253, 0.3)',
  borderRadius: '8px',
};

const kycButtonStyle = (isApproved: boolean) => ({
  padding: '8px 14px',
  background: isApproved ? '#ef4444' : '#10b981',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '14px',
  cursor: 'pointer',
  fontWeight: 500,
  transition: 'transform 0.2s, box-shadow 0.2s',
  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
});

const statusSelectStyle = {
  padding: '8px 12px',
  fontSize: '14px',
  borderRadius: '8px',
  border: '1px solid #cbd5e1',
  background: '#fff',
  cursor: 'pointer',
  fontWeight: 500,
  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  transition: 'box-shadow 0.2s',
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "NA";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "NA";
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return "NA";
  }
};

const safeDisplay = (value: any): string => {
  if (value === null || value === undefined || value === '') {
    return "NA";
  }
  return String(value).trim() || "NA";
};

// Document Type Map
const DOCUMENT_TYPE_MAP: Record<string, string> = {
  PAN: "PAN Card",
  AADHAR: "Aadhar Card",
  DRIVING_LICENSE: "Driving License",
  PASSPORT: "Passport",
  VOTER_ID: "Voter ID",
  OTHER: "Other ID"
};

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: '#3b82f6' },
  { value: 'approved', label: 'Approved', color: '#10b981' },
  { value: 'reject', label: 'Rejected', color: '#ef4444' },
  { value: 'suspect', label: 'Suspect', color: '#f59e0b' },
];

type Customer = {
  CustID: number;
  FullName: string;
  ContactNo: string;
  Email: string | null;
  Profession: string | null;
  State: string | null;
  City: string | null;
  Address: string | null;
  PinCode: string | null;
  Branch: string | null;
  Role: string | null;
  IsActive: number | boolean;
  LoginCount: number | null;
  InsertedDate: string;
  UpdatedDate: string | null;
  DOB: string | null;
  Anniversary: string | null;
  LastLoginDate: string | null;
  TinNo: string | null;
  GSTIN: string | null;
  UID: string | null;
  UPID: string | null;
  UPINumber: string | null;
  AcNo: string | null;
  BankName: string | null;
  IFCSCode: string | null;
  AcName: string | null;
  ProfilePicture: string | null;
  PANNumber: string | null;
  PANImagePath: string | null;
  IDDocType: string | null;
  IDDocNumber: string | null;
  IDDocImagePath: string | null;
  KYC: string; // 'on' or 'off'
  Status: string;
  [key: string]: any; // Allow other fields
};

type DocumentResponse = {
  success: boolean;
  documents?: {
    profilePicture: string | null;
    pan: string | null;
    idDoc: string | null;
  };
  details?: {
    PANNumber: string | null;
    IDDocType: string | null;
    IDDocNumber: string | null;
  };
  message?: string;
};

const InfluencerDetailScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [documents, setDocuments] = useState<DocumentResponse['documents'] | null>(null);
  const [docDetails, setDocDetails] = useState<DocumentResponse['details'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError("Invalid customer ID");
      setLoading(false);
      return;
    }

    const fetchCustomer = async () => {
      try {
        // Fetch customer details
        const res = await fetch('http://kkk.biznweb.in:5000/api/influencers');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        if (!Array.isArray(json.data)) {
          throw new Error("Invalid API response format");
        }

        const found = json.data.find((c: any) =>
          String(c.CustID) === String(id)
        );

        if (!found) {
          setError("Customer not found");
        } else {
          setCustomer(found);
        }
      } catch (err: any) {
        setError(err.message || "Failed to load customer data");
      }
    };

    const fetchDocuments = async () => {
      try {
        const res = await fetch(`http://kkk.biznweb.in:5000/api/get-documents/${id}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json: DocumentResponse = await res.json();

        if (json.success) {
          setDocuments(json.documents || null);
          setDocDetails(json.details || null);
        } else {
          console.error("Failed to fetch documents:", json.message);
        }
      } catch (err: any) {
        console.error("Document fetch error:", err);
        // Don't set error here as customer details might still load
      }
    };

    Promise.allSettled([fetchCustomer(), fetchDocuments()])
      .finally(() => setLoading(false));
  }, [id]);

  // Update KYC Status
  const updateKycStatus = async (newKyc: 'on' | 'off') => {
    if (!customer) return;
    
    const currentKyc = customer.KYC;
    const updatedCustomer = { ...customer, KYC: newKyc };
    setCustomer(updatedCustomer);

    try {
      const res = await fetch(`http://kkk.biznweb.in:5000/api/influencers/${customer.CustID}/kyc`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kyc: newKyc })
      });

      if (!res.ok) {
        // Revert on error
        setCustomer({ ...customer, KYC: currentKyc });
        alert("Failed to update KYC status");
      }
    } catch (err) {
      console.error("KYC update error:", err);
      setCustomer({ ...customer, KYC: currentKyc });
      alert("Failed to update KYC status");
    }
  };

  // Update Status
  const updateStatus = async (newStatus: string) => {
    if (!customer) return;
    
    const currentStatus = customer.Status;
    const updatedCustomer = { ...customer, Status: newStatus };
    setCustomer(updatedCustomer);

    try {
      const res = await fetch(`http://kkk.biznweb.in:5000/api/influencers/${customer.CustID}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (!res.ok) {
        // Revert on error
        setCustomer({ ...customer, Status: currentStatus });
        alert("Failed to update status");
      }
    } catch (err) {
      console.error("Status update error:", err);
      setCustomer({ ...customer, Status: currentStatus });
      alert("Failed to update status");
    }
  };

  // Open document in new tab
  const openDocument = (url: string) => {
    if (url) window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div style={layoutStyle}>
        <Sidebar />
        <div style={mainContainerStyle}>
          <button onClick={() => navigate(-1)} style={{ marginBottom: 20, background: "none", border: "none", fontSize: 19, cursor: "pointer", color: "#1976d2" }}>← Back</button>
          <div style={{ textAlign: 'center', padding: '40px', color: '#1976d2', fontSize: '18px' }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div style={layoutStyle}>
        <Sidebar />
        <div style={mainContainerStyle}>
          <button onClick={() => navigate(-1)} style={{ marginBottom: 20, background: "none", border: "none", fontSize: 19, cursor: "pointer", color: "#1976d2" }}>← Back</button>
          <div style={{ color: 'crimson', padding: '20px', fontSize: '16px' }}>{error || "No data"}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={layoutStyle}>
      <Sidebar />
      <div style={mainContainerStyle}>
        <button
          onClick={() => navigate(-1)}
          style={{
            marginBottom: 16,
            background: "none",
            border: "none",
            fontSize: 18,
            cursor: "pointer",
            color: "#1976d2",
            textAlign: 'left',
            fontWeight: 500,
            padding: '8px 12px',
            borderRadius: '6px',
            transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#e3f2fd'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          ← Back
        </button>

        {/* Profile Section */}
        <div style={cardStyle} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
          <div style={sectionTitleStyle}>
            Profile Information
          </div>
          <div style={imageContainerStyle}>
            <img
              src={documents?.profilePicture || customer.ProfilePicture || "/assets/default-profile.jpg"}
              alt="Profile"
              style={profileImageStyle}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              onError={(e) => {
                (e.target as HTMLImageElement).src = "/assets/default-profile.jpg";
              }}
            />
            <div>
              <div style={{ fontSize: '20px', fontWeight: 600, color: '#333', marginBottom: '4px' }}>{safeDisplay(customer.FullName)}</div>
              <div style={{ fontSize: '15px', color: '#666', marginTop: '2px' }}>Cust ID: {safeDisplay(customer.CustID)}</div>
              <div style={actionContainerStyle}>
                <div style={{ fontSize: '14px', color: '#555', fontWeight: 500 }}>
                  Status: 
                </div>
                <select
                  value={customer.Status}
                  onChange={(e) => updateStatus(e.target.value)}
                  style={statusSelectStyle}
                  onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)'}
                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)'}
                >
                  {STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value} style={{ color: opt.color }}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div style={actionContainerStyle}>
                <div style={{ fontSize: '14px', color: '#555', fontWeight: 500 }}>
                  KYC: {customer.KYC === 'on' ? 'Approved' : 'Not Approved'}
                </div>
                <button
                  onClick={() => updateKycStatus(customer.KYC === 'on' ? 'off' : 'on')}
                  style={kycButtonStyle(customer.KYC === 'on')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
                  }}
                >
                  {customer.KYC === 'on' ? 'Revoke' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>Contact No</div>
            <div style={valueStyle}>{safeDisplay(customer.ContactNo)}</div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>Email</div>
            <div style={valueStyle}>{safeDisplay(customer.Email)}</div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>Profession</div>
            <div style={valueStyle}>{safeDisplay(customer.Profession)}</div>
          </div>
        </div>

        {/* Documents Section */}
        {(documents?.pan || documents?.idDoc || docDetails?.PANNumber || docDetails?.IDDocNumber) && (
          <div style={cardStyle} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
            <div style={sectionTitleStyle}>
              Documents
            </div>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              {documents?.pan && (
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '6px', color: '#333' }}>
                    PAN Card: {safeDisplay(docDetails?.PANNumber)}
                  </div>
                  <img
                    src={documents.pan}
                    alt="PAN"
                    style={documentImageStyle}
                    onClick={() => openDocument(documents.pan!)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "/assets/no-image.jpg";
                    }}
                  />
                </div>
              )}
              {documents?.idDoc && (
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '6px', color: '#333' }}>
                    {docDetails?.IDDocType ? DOCUMENT_TYPE_MAP[docDetails.IDDocType] || docDetails.IDDocType : "ID Document"}: {safeDisplay(docDetails?.IDDocNumber)}
                  </div>
                  <img
                    src={documents.idDoc}
                    alt="ID Document"
                    style={documentImageStyle}
                    onClick={() => openDocument(documents.idDoc!)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "/assets/no-image.jpg";
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Address Information */}
        <div style={cardStyle} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
          <div style={sectionTitleStyle}>
            Address Information
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>Address</div>
            <div style={valueStyle}>{safeDisplay(customer.Address)}</div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>City</div>
            <div style={valueStyle}>{safeDisplay(customer.City)}</div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>State</div>
            <div style={valueStyle}>{safeDisplay(customer.State)}</div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>Pin Code</div>
            <div style={valueStyle}>{safeDisplay(customer.PinCode)}</div>
          </div>
        </div>

        {/* Financial & IDs */}
        <div style={cardStyle} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
          <div style={sectionTitleStyle}>
            Financial & IDs
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>TIN No</div>
            <div style={valueStyle}>{safeDisplay(customer.TinNo)}</div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>GSTIN</div>
            <div style={valueStyle}>{safeDisplay(customer.GSTIN)}</div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>UID</div>
            <div style={valueStyle}>{safeDisplay(customer.UID)}</div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>UPID</div>
            <div style={valueStyle}>{safeDisplay(customer.UPID)}</div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>UPI Number</div>
            <div style={valueStyle}>{safeDisplay(customer.UPINumber)}</div>
          </div>
        </div>

        {/* Bank Details */}
        <div style={cardStyle} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
          <div style={sectionTitleStyle}>
            Bank Details
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>Account No</div>
            <div style={valueStyle}>{safeDisplay(customer.AcNo)}</div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>Bank Name</div>
            <div style={valueStyle}>{safeDisplay(customer.BankName)}</div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>IFSC Code</div>
            <div style={valueStyle}>{safeDisplay(customer.IFCSCode)}</div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>Account Name</div>
            <div style={valueStyle}>{safeDisplay(customer.AcName)}</div>
          </div>
        </div>

        {/* System Information */}
        <div style={cardStyle} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
          <div style={sectionTitleStyle}>
            System Information
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>Branch</div>
            <div style={valueStyle}>{safeDisplay(customer.Branch)}</div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>Role</div>
            <div style={valueStyle}>{safeDisplay(customer.Role)}</div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>Is Active</div>
            <div style={valueStyle}>{customer.IsActive ? "Yes" : "No"}</div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>Login Count</div>
            <div style={valueStyle}>{safeDisplay(customer.LoginCount)}</div>
          </div>
        </div>

        {/* Dates */}
        <div style={cardStyle} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
          <div style={sectionTitleStyle}>
            Important Dates
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>Inserted Date</div>
            <div style={valueStyle}>{formatDate(customer.InsertedDate)}</div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>Updated Date</div>
            <div style={valueStyle}>{formatDate(customer.UpdatedDate)}</div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>DOB</div>
            <div style={valueStyle}>{formatDate(customer.DOB)}</div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>Anniversary</div>
            <div style={valueStyle}>{formatDate(customer.Anniversary)}</div>
          </div>
          <div style={fieldRowStyle} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <div style={labelStyle}>Last Login</div>
            <div style={valueStyle}>{formatDate(customer.LastLoginDate)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfluencerDetailScreen;